import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandPaper,
  faPlayCircle,
  faSpinner,
  faCheck,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

// Icon mapping for FontAwesome
const iconMap = {
  'hand-wave': faHandPaper,
  'play-circle': faPlayCircle,
  'spinner': faSpinner,
  'check': faCheck,
  'exclamation-triangle': faExclamationTriangle
};

const BackendActionRenderer = ({ definition, agentId, token }) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleAction = async () => {
    if (definition.confirmation?.enabled) {
      setShowConfirmation(true);
      return;
    }
    
    await executeAction();
  };

  const executeAction = async () => {
    try {
      setLoading(true);
      setShowConfirmation(false);

      const action = definition.actions.primary;
      const endpoint = action.endpoint.replace('{agent_id}', agentId);
      
      const response = await fetch(endpoint, {
        method: action.method || 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        ...(action.method === 'POST' && action.payload ? {
          body: JSON.stringify(action.payload)
        } : {})
      });

      const result = await response.json();

      if (result.success) {
        toast.success(action.success_message || 'Action completed successfully!');
        
        // Handle redirect if specified
        if (action.redirect_on_success) {
          const redirectUrl = action.redirect_on_success.replace('{agent_id}', agentId);
          window.location.href = redirectUrl;
        }
      } else {
        toast.error(action.error_message || 'Action failed. Please try again.');
      }

    } catch (error) {
      console.error('Backend action error:', error);
      toast.error(definition.actions.primary.error_message || 'Action failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderButton = () => {
    const config = definition.config || {};
    const content = definition.content || {};
    const loadingState = definition.loading_state || {};

    // Build button styles
    const buttonStyles = {
      width: config.width || 'auto',
      ...(config.custom_css && { cssText: config.custom_css })
    };

    const buttonClasses = [
      'btn',
      config.button_style === 'primary' ? 'btn-primary' : 'btn-secondary',
      config.size === 'large' ? 'btn-lg' : '',
      loading ? loadingState.css_class || 'opacity-75 cursor-not-allowed' : '',
      'transition-all duration-200'
    ].filter(Boolean).join(' ');

    return (
      <button
        onClick={handleAction}
        disabled={loading}
        className={buttonClasses}
        style={buttonStyles}
        onMouseEnter={(e) => {
          if (config.hover_effects && !loading) {
            e.target.style.cssText += config.hover_effects;
          }
        }}
        onMouseLeave={(e) => {
          if (config.hover_effects && !loading) {
            e.target.style.cssText = config.custom_css || '';
          }
        }}
      >
        {loading ? (
          <>
            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
            {loadingState.text || 'Loading...'}
          </>
        ) : (
          <>
            {content.sections?.map((section, index) => (
              <span key={index}>
                {section.type === 'icon' && (
                  <FontAwesomeIcon 
                    icon={iconMap[section.icon] || faPlayCircle}
                    className={section.css_class || ''}
                    style={{ fontSize: section.style?.replace('font-size:', '').replace(';', '') }}
                  />
                )}
                {section.type === 'text' && (
                  <span className={section.css_class || ''}>
                    {section.content}
                  </span>
                )}
              </span>
            ))}
          </>
        )}
      </button>
    );
  };

  const renderConfirmationModal = () => {
    if (!showConfirmation) return null;

    const confirmation = definition.confirmation;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {confirmation.title || 'Confirm Action'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {confirmation.message || 'Are you sure you want to continue?'}
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowConfirmation(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              {confirmation.cancel_text || 'Cancel'}
            </button>
            <button
              onClick={executeAction}
              className="btn btn-primary"
            >
              {confirmation.confirm_text || 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 text-center">
      {renderButton()}
      {renderConfirmationModal()}
    </div>
  );
};

export default BackendActionRenderer;